import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import { previaProjecaoTransferenciaTipo } from "@/modules/financeiro-obras";
import type {
  DecisaoEscopoTransferenciaTipo,
  DecisaoProjecaoTransferencia,
  PreviaProjecaoTransferenciaTipo,
} from "@/modules/financeiro-obras";
import {
  PREVIA_TIPO_ERRO,
  PREVIA_TIPO_PERMISSAO,
} from "./revisao-transferencia-tipo/revisao-transferencia-tipo.constants";

interface Params {
  obraId: string;
  obraTipoObraId: string;
  destinoId: string | null;
  centroCustoId: string | null;
  escopoIds: string[];
  ativa: boolean;
}

type Decisoes = Record<string, DecisaoProjecaoTransferencia>;

export function useRevisaoProjecaoTransferenciaTipo({
  obraId,
  obraTipoObraId,
  destinoId,
  centroCustoId,
  escopoIds,
  ativa,
}: Params) {
  const chave = destinoId && centroCustoId ? `${destinoId}|${centroCustoId}` : null;
  const [guardadas, setGuardadas] = useState<{ chave: string; valor: Decisoes } | null>(null);
  const decisoes: Decisoes = guardadas && guardadas.chave === chave ? guardadas.valor : {};
  const consultar = ativa && chave !== null && escopoIds.length > 0;

  const previa = useAsyncQuery<PreviaProjecaoTransferenciaTipo | null>({
    fetcher: (signal) =>
      previaProjecaoTransferenciaTipo(
        obraId,
        obraTipoObraId,
        {
          obraDestinoId: destinoId ?? "",
          centroCustoDestinoId: centroCustoId ?? "",
          escopos: corpoDosEscopos(escopoIds, decisoes),
        },
        { signal },
      ),
    queryKey: `previa-transferencia-tipo/${obraId}/${obraTipoObraId}/${chave}/${escopoIds.join(",")}/${JSON.stringify(decisoes)}`,
    initialData: null,
    enabled: consultar,
    keepPrevious: true,
    forbiddenMessage: PREVIA_TIPO_PERMISSAO,
    errorMessage: PREVIA_TIPO_ERRO,
    resolveErrorMessage: getErrorMessage,
  });

  const falhou = previa.status === "error" || previa.status === "forbidden";
  let estado: "inativo" | "carregando" | "erro" | "pronto" = "pronto";
  if (!consultar) estado = "inativo";
  else if (falhou) estado = "erro";
  else if (previa.status === "loading") estado = "carregando";

  return {
    estado,
    errorMessage: falhou ? previa.errorMessage : null,
    previas: previa.data?.escopos ?? [],
    decisoes,
    aplicarDecisao: (escopoId: string, decisao: DecisaoProjecaoTransferencia | null) => {
      if (!chave) return;
      const { [escopoId]: _anterior, ...demais } = decisoes;
      setGuardadas({ chave, valor: decisao ? { ...demais, [escopoId]: decisao } : demais });
    },
    recarregar: previa.reload,
  };
}

export function corpoDosEscopos(
  escopoIds: string[],
  decisoes: Decisoes,
): DecisaoEscopoTransferenciaTipo[] {
  return escopoIds.map((obraEscopoMacroId) => {
    const projecao = decisoes[obraEscopoMacroId];
    return projecao ? { obraEscopoMacroId, projecao } : { obraEscopoMacroId };
  });
}
